import Permissions from './Permissions.js'
import {showResetPasswordModal} from './ResetPasswordModal.js'
import {showArchiveUserModal} from './ArchiveUserModal.js'
import className from '../../../common/className.js'
import {buildPath} from '../../../common/querystring.js'
import {
  SETTINGS,
  USER_SETTINGS_LINK,
  USERS,
} from '../../../common/constants/SettingsPanels.js'
import {
  saveUser,
  settingsUserIDSelector,
  updatePermissions,
  updateUsersForm,
  userSettingsErrorsSelector,
  usersFormSelector,
} from './usersFunctions.js'
import {getState, useSelector} from '../../../store.js'
import FormList from '../Common/FormList.js'
import FormListItem from '../Common/FormListItem.js'
import TextInput from '../Common/TextInput.js'
import Checkbox from '../Common/Checkbox.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'
import LinkButton from '../../../common/components/Link/LinkButton.js'
import {canManageUserPermissionSelector, meSelector} from '../../../data/me.js'
import {usePermissionsSelector} from '../../../data/company.js'
import {showSetupMFAModal} from './SetupMFAModal.js'
import {showConfirmMessageModal} from '../../../common/components/Modal/ConfirmMessageModal.js'
import api from '../../../common/api.js'
import {showGlobalError} from '../../GlobalErrorMessage.js'
import {setUser, userSelector} from '../../../data/users.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import Icon from '../../../common/icons/Icon.js'

async function showDisableMFAModal(userID) {
  const meID = meSelector(getState()).id
  const user = userSelector(getState(), {userID})

  const willRemove = await showConfirmMessageModal({
    title: 'Disable Multi-Factor Auth',
    message: `Do you want to disable MFA ${meID === user.id ? 'on your account' : `for ${user.name}`}?`,
  })

  if (!willRemove) {
    return
  }

  try {
    const {json} = await api.put(`/user/${userID}`, {otp_seed: null})

    setUser(json)

    showMessageToast('MFA Disabled')
  } catch (err) {
    showGlobalError(
      {
        summary: 'Error disabling MFA.',
        details: err.message || err.error_message,
      },
      err,
    )
  }
}

export default function EditForm({user}) {
  const form = useSelector(usersFormSelector)
  const userID = useSelector(settingsUserIDSelector)
  const errors = useSelector(userSettingsErrorsSelector)
  const meID = useSelector(meSelector).id
  const canManageUser = useSelector(canManageUserPermissionSelector)
  const usePermissions = useSelector(usePermissionsSelector)

  let editLink
  let isExpanded = false

  const isSelf = meID === user.id
  if (canManageUser || isSelf) {
    editLink = `#${buildPath([SETTINGS, USERS, user.id])}`
    isExpanded = user.id === userID
  }

  return (
    <li
      className={className`settings-list-item clearfix ${{
        expanded: isExpanded,
      }}`}
    >
      <strong className="settings-list-item-title medium-5 small-8 columns">
        {user.name}
      </strong>
      <span className="settings-list-item-desc medium-5 hide-for-small columns">
        {user.email}
      </span>
      <span className="medium-2 small-4 columns right-aligned-text right">
        {editLink && (
          <a
            type="button"
            className="inline-text-button settings-list-item-button"
            href={editLink}
          >
            <Icon name="sym-edit" className="fs-01 margin-right-2" />
            <span className="button-text">Edit</span>
          </a>
        )}
      </span>
      {isExpanded && (
        <form className="settings-list-item-form-wrap padding-bottom-10 clear-both">
          <fieldset>
            {errors.serverError && (
              <div className="row">
                <ul className="medium-5 columns error-list">
                  <li>{errors.serverError}</li>
                </ul>
              </div>
            )}
            <FormList className="medium-5 columns margin-left-0">
              <FormListItem className="bigger margin-bottom-20">
                <TextInput
                  label="Name"
                  required
                  name="user_name"
                  value={form.name || ''}
                  onChange={(event) =>
                    updateUsersForm({
                      name: event.target.value,
                      hasChanged__name: true,
                    })
                  }
                  errorMessage={form.hasChanged__name && errors.name}
                />
              </FormListItem>
              <FormListItem className="bigger margin-bottom-25">
                <TextInput
                  label="Email"
                  required
                  name="user_email"
                  value={form.email || ''}
                  onChange={(event) =>
                    updateUsersForm({
                      email: event.target.value,
                      hasChanged__email: true,
                    })
                  }
                  errorMessage={form.hasChanged__email && errors.email}
                />
              </FormListItem>
              {usePermissions && (
                <FormListItem className="margin-bottom-20">
                  <Checkbox
                    label="Admin"
                    id="permission_can_manage_user"
                    checked={!!form?.permissions?.can_manage_user}
                    onChange={(checked) =>
                      updatePermissions(['can_manage_user'], checked)
                    }
                    disabled={!(canManageUser && !isSelf)}
                  />
                </FormListItem>
              )}
              {isSelf ? (
                <>
                  <FormListItem className="margin-bottom-20">
                    <ButtonPrimary
                      size="sm"
                      isOutlined
                      onClick={() =>
                        updateUsersForm({
                          isChangingPassword: !form.isChangingPassword,
                        })
                      }
                    >
                      <strong>Change password</strong>
                    </ButtonPrimary>
                  </FormListItem>
                  {form.isChangingPassword && (
                    <>
                      <FormListItem className="bigger margin-bottom-20">
                        <TextInput
                          label="Password"
                          required
                          type="password"
                          name="user_password"
                          value={form.password || ''}
                          onChange={(event) =>
                            updateUsersForm({
                              password: event.target.value,
                              hasChanged__password: true,
                            })
                          }
                          errorMessage={
                            form.hasChanged__password && errors.password
                          }
                        />
                      </FormListItem>
                      <FormListItem className="bigger margin-bottom-15">
                        <TextInput
                          label="Retype New Password"
                          required
                          type="password"
                          name="user_passwordConfirm"
                          value={form.passwordConfirm || ''}
                          onChange={(event) =>
                            updateUsersForm({
                              passwordConfirm: event.target.value,
                              hasChanged__passwordConfirm: true,
                            })
                          }
                          errorMessage={
                            form.hasChanged__passwordConfirm &&
                            errors.passwordConfirm
                          }
                        />
                      </FormListItem>
                    </>
                  )}
                </>
              ) : (
                <FormListItem className="margin-bottom-20">
                  <ButtonPrimary
                    size="sm"
                    isOutlined
                    onClick={() => showResetPasswordModal(user.email)}
                  >
                    <strong>Reset password</strong>
                  </ButtonPrimary>
                </FormListItem>
              )}
              {usePermissions && canManageUser && !isSelf && (
                <FormListItem className="margin-bottom-20">
                  <ButtonPrimary
                    size="sm"
                    isOutlined
                    onClick={() => showArchiveUserModal(user.id)}
                  >
                    <strong>Archive User</strong>
                  </ButtonPrimary>
                </FormListItem>
              )}
              {(isSelf || canManageUser) && (
                <FormListItem className="divider--top margin-bottom-20">
                  <p className="fs-01 lh-md margin-bottom-5">
                    <strong>Multi-Factor Authorization (MFA)</strong>
                  </p>

                  {!user.otp_required ? (
                    <>
                      <p className="fs-n0 lh-md margin-bottom-15">
                        Add an extra layer of security to this user account by
                        enabling MFA. Access to a third-party Authenticator app
                        is required to utilize MFA in Ordoro. Check out this{' '}
                        <a
                          className="btn btn--link v-align-base darkest border-underline"
                          href="https://support.ordoro.com/how-to-enable-multi-factor-authentication-mfa-in-ordoro/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <strong>handy support article</strong>
                        </a>{' '}
                        for help getting MFA configured.
                      </p>
                      <ButtonPrimary
                        size="sm"
                        isOutlined
                        onClick={() => showSetupMFAModal(user.id)}
                      >
                        <strong>Enable MFA</strong>
                      </ButtonPrimary>
                    </>
                  ) : (
                    <>
                      <p className="fs-00 lh-md margin-top-15 margin-bottom-15">
                        MFA is enabled for this user account.
                      </p>
                      <p className="fs-n0 lh-md margin-bottom-15">
                        Entering a valid 6-digit OTP code (provided by the
                        connected Authenticator app) will now also be required
                        while logging in to Ordoro.
                      </p>
                      <ButtonPrimary
                        size="sm"
                        isOutlined
                        onClick={() => showDisableMFAModal(user.id)}
                      >
                        <strong>Disable MFA</strong>
                      </ButtonPrimary>
                    </>
                  )}
                </FormListItem>
              )}
            </FormList>
            {usePermissions && canManageUser && !isSelf && (
              <div className="clearfix clear-both">
                <Permissions permissions={form.permissions} />
              </div>
            )}
            <div className="clearfix clear-both">
              <div className="medium-7 columns">
                <div className="divider--top padding-top-20">
                  <ButtonPrimary
                    isDisabled={errors.preventSave}
                    isLoading={form.isSaving}
                    onClick={saveUser}
                  >
                    Save
                  </ButtonPrimary>
                  <LinkButton href={USER_SETTINGS_LINK} mode="secondary">
                    Cancel
                  </LinkButton>
                </div>
              </div>
            </div>
          </fieldset>
        </form>
      )}
    </li>
  )
}
